import { useState, useMemo, useEffect } from 'react'
import capitalize from 'lodash/capitalize'
import styled from 'styled-components'
import { dayjsLondon } from 'lib/dayjs'
import {
  Button,
  H,
  P,
  Wrapper,
  TickIcon,
  RadioGroup,
  SvgIcon,
} from '@farewill/ui'
import { COLOR, BORDER, GTR, FONT } from '@farewill/ui/tokens'
import { Checkbox } from 'components/form'
import { ConfirmModal } from 'components/confirm-modal'
import { showModal } from 'state/actions'
import { useDispatch } from 'react-redux'
import {
  type Provider,
  type Mode,
} from '../helpers/complete-sections/section-referral'
import {
  type AutomatedFormState,
  type ProbateCheckProvider,
} from './acting-executors-checks'

const StyledRadioWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: ${GTR.M};
  margin-bottom: ${GTR.S};
`

const AutoModePanel = styled(Wrapper)`
  border-radius: ${BORDER.RADIUS.M};
  background-color: ${COLOR.BACKGROUND.SMOKE};
  padding: ${GTR.M};
  margin-top: ${GTR.S};
`

const AutoModeSentPanel = styled(Wrapper)`
  border-radius: ${BORDER.RADIUS.M};
  background-color: ${COLOR.BACKGROUND.SMOKE};
  padding: ${GTR.S};
  margin-top: ${GTR.S};
  display: flex;
  align-items: center;
  gap: ${GTR.S};
`

const AutoModeReceivedPanel = styled(Wrapper)`
  border-radius: ${BORDER.RADIUS.M};
  background-color: ${COLOR.STATE.SUCCESS_20};
  padding: ${GTR.S};
  margin-top: ${GTR.S};
  display: flex;
  align-items: center;
  gap: ${GTR.S};
`

const ManualModePanel = styled(Wrapper)<{ $isComplete: boolean }>`
  border-radius: ${BORDER.RADIUS.M};
  background-color: ${({ $isComplete }) =>
    $isComplete ? COLOR.STATE.SUCCESS_20 : COLOR.BACKGROUND.SMOKE};
  padding: ${GTR.S};
  margin-top: ${GTR.S};
`

const CheckboxWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: ${GTR.S};
`

const StyledButtonUnderline = styled(Button.Underline)`
  padding: 0;
  font-weight: ${FONT.WEIGHT.REGULAR};
`

const IconWrapper = styled.div<{ $bgColor: string }>`
  width: ${GTR.L};
  height: ${GTR.L};
  border-radius: 50%;
  background-color: ${({ $bgColor }) => $bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const StyledFlexWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: ${GTR.M};
`

interface ActingExecutorChecksProps {
  testId: string
  provider: Provider
  personId: number
  email: string | undefined
  formTitle: string
  automatedForm: ProbateCheckProvider['automatedForm']
  manualForm: ProbateCheckProvider['manualForm'] & {
    sentValue: Date | null
    confirmedValue: Date | null
  }
  detectMode: ProbateCheckProvider['detectMode']
}

export const ActingExecutorChecks = ({
  testId,
  provider,
  personId,
  email,
  formTitle,
  automatedForm,
  manualForm,
  detectMode,
}: ActingExecutorChecksProps) => {
  const dispatch = useDispatch()
  const [autoFormState, setAutoFormState] = useState<AutomatedFormState | null>(
    null
  )

  useEffect(() => {
    const fetchAutomatedFormState = async () => {
      const autoFormState = await automatedForm.state(personId)
      setAutoFormState(autoFormState)
      setMode(detectMode(personId, autoFormState, manualForm.sentValue))
    }
    if (!autoFormState) {
      fetchAutomatedFormState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, automatedForm.state, manualForm.sentValue, detectMode])

  const [isSending, setIsSending] = useState(false)
  const [mode, setMode] = useState<Mode>(
    detectMode(personId, autoFormState, manualForm.sentValue)
  )
  const providerName = provider === 'docusign' ? 'Docusign' : 'Onfido'

  const { sentAt: autoSentAt, receivedAt: autoReceivedAt } = autoFormState || {
    sentAt: null,
    receivedAt: null,
  }

  const isManualSent = !!manualForm.sentValue
  const isManualConfirmed = !!manualForm.confirmedValue
  const isManualComplete = useMemo(
    () => isManualSent && isManualConfirmed,
    [isManualSent, isManualConfirmed]
  )

  const handleAutoFormSubmit = async () => {
    setIsSending(true)
    try {
      const updatedState = await automatedForm.submit(personId)
      setAutoFormState(updatedState)
    } finally {
      setIsSending(false)
    }
  }

  const handleManualSendToggle = async (
    personId: number,
    date: Date | null
  ) => {
    await manualForm.sendToggle(personId, date)
    const updatedState = await automatedForm.state(personId)
    setAutoFormState(updatedState)
    return
  }

  return (
    <Wrapper data-testid={testId}>
      <StyledRadioWrapper>
        <P margin={['M', '0', '0']}>{formTitle}:</P>
        <RadioGroup
          name={`${personId}-${provider}-mode`}
          size="S"
          horizontal
          options={[
            { label: 'Via ' + providerName, value: 'auto' },
            { label: 'Manually', value: 'manual' },
          ]}
          value={mode || ''}
          onChange={(e) => setMode(e.target.value as Mode)}
          margin={['0', '0']}
        />
      </StyledRadioWrapper>

      {mode === 'auto' && (
        <>
          {autoReceivedAt ? (
            <AutoModeReceivedPanel>
              <IconWrapper $bgColor={COLOR.STATE.SUCCESS}>
                <TickIcon color={COLOR.WHITE} />
              </IconWrapper>
              <P>
                Completed via {providerName} on{' '}
                {dayjsLondon(autoReceivedAt).format('Do MMMM YYYY')}
              </P>
            </AutoModeReceivedPanel>
          ) : autoSentAt ? (
            <AutoModeSentPanel>
              <IconWrapper $bgColor={COLOR.STATE.SUCCESS_60}>
                <SentIcon color={COLOR.STATE.SUCCESS_60} />
              </IconWrapper>
              <P margin="0">
                Sent on {dayjsLondon(autoSentAt).format('Do MMMM YYYY')}
              </P>
              <StyledButtonUnderline
                loading={isSending}
                disabled={isSending}
                onClick={() =>
                  dispatch(
                    showModal({
                      component: () => (
                        <ConfirmModal
                          onConfirm={handleAutoFormSubmit}
                          confirmButtonText="Resend"
                          confirmButtonSubmittingText="Resending..."
                        >
                          <>
                            <P>
                              This document was sent on{' '}
                              {dayjsLondon(autoSentAt).format('Do MMMM YYYY')}.
                            </P>
                            <P>Are you sure you want to resend the document?</P>
                          </>
                        </ConfirmModal>
                      ),
                    })
                  )
                }
              >
                {isSending ? 'Sending...' : 'Resend via ' + providerName}
              </StyledButtonUnderline>
            </AutoModeSentPanel>
          ) : !email ? (
            <AutoModePanel>
              <H size="XS">{automatedForm.title}</H>
              <P color="GREY.DARK" margin={['XS', 0, 'M']}>
                An email address is required to send via {providerName}. Please
                add an email for this person first.
              </P>
              <Button.Primary disabled>Send now</Button.Primary>
            </AutoModePanel>
          ) : (
            <AutoModePanel>
              <H size="XS">{automatedForm.title}</H>
              <P color="GREY.DARK" margin={['XS', 0, 'M']}>
                {automatedForm.description}
              </P>
              <Button.Primary
                onClick={handleAutoFormSubmit}
                loading={isSending}
                disabled={isSending}
              >
                {isSending ? 'Sending...' : 'Send now'}
              </Button.Primary>
            </AutoModePanel>
          )}
        </>
      )}

      {mode === 'manual' && (
        <ManualModePanel $isComplete={isManualComplete}>
          {!isManualComplete && (
            <>
              <H size="XS">{manualForm.title}</H>
              <P color="GREY.DARK" margin={['XS', 0, 'M']}>
                {manualForm.description}
              </P>
            </>
          )}
          <StyledFlexWrapper>
            <CheckboxWrapper>
              <Checkbox
                name="sendAt"
                disabled={!!manualForm.confirmedValue}
                checked={!!manualForm.sentValue}
                onChange={(e) => {
                  return handleManualSendToggle(
                    personId,
                    e.target.checked ? new Date() : null
                  )
                }}
                label="Sent"
              />
              <Checkbox
                name="confirmedAt"
                checked={!!manualForm.confirmedValue}
                disabled={!manualForm.sentValue}
                onChange={(e) => {
                  manualForm.confirmToggle(
                    personId,
                    e.target.checked ? new Date() : null
                  )
                }}
                label={capitalize(manualForm.confirmedAction)}
              />
            </CheckboxWrapper>

            {isManualSent && !isManualConfirmed && (
              <P margin="0" color={COLOR.GREY.MEDIUM}>
                Marked as sent on{' '}
                {dayjsLondon(manualForm.sentValue).format('Do MMMM YYYY')}
              </P>
            )}
            {isManualConfirmed && (
              <P margin="0" color={COLOR.GREY.DARK}>
                Marked as {manualForm.confirmedAction} on{' '}
                {dayjsLondon(manualForm.confirmedValue).format('Do MMMM YYYY')}
              </P>
            )}
          </StyledFlexWrapper>
        </ManualModePanel>
      )}
    </Wrapper>
  )
}

const SentIcon = ({ color }: { color: string }) => (
  <SvgIcon
    width={28}
    height={28}
    viewBox="0 0 28 28"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="14" cy="14" r="14" fill={color} />
    <path
      d="M21.0146 8.50098C21.2715 8.47965 21.5183 8.60799 21.6475 8.83105C21.7766 9.05467 21.7645 9.33385 21.6172 9.5459L14.6562 19.5625C14.5094 19.7738 14.2538 19.8817 14 19.8398C13.7461 19.7977 13.5387 19.6127 13.4678 19.3652L12.04 14.3779L8.43457 10.6484C8.25613 10.4634 8.19981 10.1919 8.29004 9.95117C8.38062 9.71052 8.60218 9.54317 8.8584 9.52148L21.0146 8.50098ZM10.5146 17.2891C10.8335 17.1052 11.2408 17.2144 11.4248 17.5332C11.6088 17.852 11.4995 18.2593 11.1807 18.4434L9.75684 19.2656C9.43797 19.4497 9.0298 19.3403 8.8457 19.0215C8.66198 18.7027 8.77124 18.2954 9.08984 18.1113L10.5146 17.2891ZM10.0762 14.7471C10.3843 14.6088 10.7533 14.7236 10.9258 15.0225C11.0982 15.3212 11.013 15.6978 10.7393 15.8955L10.6816 15.9326L7.83301 17.5781C7.51417 17.7622 7.10598 17.6528 6.92188 17.334C6.73778 17.0151 6.84716 16.6069 7.16602 16.4229L10.0146 14.7783L10.0762 14.7471ZM13.4199 14.3516L14.3623 17.6484L18.8203 11.2334L13.4199 14.3516ZM8.09082 13.0898C8.40959 12.9061 8.81694 13.0153 9.00098 13.334C9.18507 13.6528 9.0757 14.061 8.75684 14.2451L7.33301 15.0674C7.01417 15.2515 6.60598 15.142 6.42188 14.8232C6.23778 14.5044 6.34716 14.0962 6.66602 13.9121L8.09082 13.0898ZM10.3701 10.7324L12.7539 13.1973L18.1553 10.0781L10.3701 10.7324Z"
      fill="#fff"
    />
  </SvgIcon>
)
